/* eslint-disable @typescript-eslint/no-explicit-any */

interface ScoreTypeDetail {
  label: string;
  shortName: string;
  scoreKey: string;
  apiType: string | null;
  reportLabel?: string;
  sentimentType: 'pos' | 'neg' | '';
}

const SCORE_TYPE_MAPPING: Record<string, ScoreTypeDetail> = {
  volume: {
    label: 'Volume',
    shortName: 'Vol',
    scoreKey: 'volume',
    apiType: null,
    sentimentType: '',
  },
  sentiment: {
    label: 'Sentiment',
    shortName: 'Sent',
    scoreKey: 'sentiment',
    apiType: null,
    sentimentType: 'pos',
  },
  general_risk: {
    label: 'General Risk',
    shortName: 'GR',
    scoreKey: 'general_risk',
    apiType: 'general_risk',
    sentimentType: 'neg',
  },
  // Canonical cockpit id is `credit_risk`; the wire name `credit_watch` lives
  // only as the `scoreKey`/`apiType` value (produced at the translation points).
  credit_risk: {
    label: 'Credit Risk',
    shortName: 'CR',
    scoreKey: 'credit_watch',
    apiType: 'credit_watch',
    sentimentType: 'neg',
  },
  esg_risk: {
    label: 'ESG Risk',
    shortName: 'ESGR',
    scoreKey: 'esg_risk',
    apiType: 'esg_risk',
    sentimentType: 'neg',
  },
  esg: {
    label: 'ESG',
    shortName: 'ESG',
    scoreKey: 'esg',
    apiType: 'esg',
    sentimentType: 'pos',
  },
  e_standard: {
    label: 'Environmental',
    shortName: 'E',
    scoreKey: 'e_standard',
    apiType: 'e_standard',
    sentimentType: 'pos',
  },
  s_standard: {
    label: 'Social',
    shortName: 'S',
    scoreKey: 's_standard',
    apiType: 's_standard',
    sentimentType: 'pos',
  },
  g_standard: {
    label: 'Governance',
    shortName: 'G',
    scoreKey: 'g_standard',
    apiType: 'g_standard',
    sentimentType: 'pos',
  },
  immediate_risk: {
    label: 'Immediate Risk',
    shortName: 'IR',
    scoreKey: 'immediate_risk',
    apiType: 'immediate_risk',
    sentimentType: 'neg',
  },
  lksg_risk: {
    label: 'LkSG Risk',
    shortName: 'LkSG',
    scoreKey: 'lksg_risk',
    apiType: 'lksg_risk',
    sentimentType: 'neg',
  },
  // Canonical cockpit id is `bb`; the wire names (`bull_bear` for the sort/
  // endpoint param, `investment` for reports) live behind the translation points.
  bb: {
    reportLabel: 'Investment',
    label: 'Bull-Bear',
    shortName: 'BB',
    scoreKey: 'bb',
    apiType: 'investment',
    sentimentType: 'neg',
  },
  eb_sim: {
    label: 'EB-SIM',
    shortName: 'EB-SIM',
    scoreKey: 'eb_sim',
    apiType: 'eb_sim',
    sentimentType: 'pos',
  },
  eb_sim_risk: {
    label: 'EB-SIM Risk',
    shortName: 'EB-SIMR',
    scoreKey: 'eb_sim_risk',
    apiType: 'eb_sim_risk',
    sentimentType: 'neg',
  },
};

const EMPTY_SCORE_TYPE_DETAIL: ScoreTypeDetail = {
  label: '',
  shortName: '',
  scoreKey: '',
  apiType: null,
  reportLabel: '',
  sentimentType: '',
};

// Wire/legacy score names → the canonical cockpit id. There is now ONE detail
// record per score (no drift-prone duplicate entries); lookups by the wire name
// resolve through this alias so existing wire-keyed consumers keep working.
const SCORE_TYPE_ALIASES: Record<string, string> = {
  credit_watch: 'credit_risk',
  bull_bear: 'bb',
};

// Wire/legacy name → canonical cockpit id (`bull_bear`→`bb`, `credit_watch`→
// `credit_risk`); unknown ids pass through unchanged.
export const resolveScoreTypeKey = (scoreType: string): string =>
  SCORE_TYPE_MAPPING[scoreType]
    ? scoreType
    : (SCORE_TYPE_ALIASES[scoreType] ?? scoreType);

const getScoreTypeDetails = (scoreType: string): ScoreTypeDetail =>
  SCORE_TYPE_MAPPING[resolveScoreTypeKey(scoreType)] ?? EMPTY_SCORE_TYPE_DETAIL;

export function isKnownScoreType(scoreType: string): boolean {
  return Object.prototype.hasOwnProperty.call(
    SCORE_TYPE_MAPPING,
    resolveScoreTypeKey(scoreType),
  );
}

// ---------------------------------------------------------------------------
// Score-info helpers (pure functions — no auth concern).
// Each takes the user's `scorelabSettings` and returns display info.
// ---------------------------------------------------------------------------

export interface ScoreInfo {
  name: string;
  full: string;
  short: string;
}

export interface ScoreInfoList {
  yukka: ScoreInfo[];
  custom: ScoreInfo[];
}

const EMPTY_SCORE_NAME: ScoreInfo = {
  name: '',
  full: 'Score',
  short: 'Score',
};

/**
 * Yukka scores (in display order) with their access-flag mapping. eb_sim and
 * eb_sim_risk share a single access flag because they're licensed together.
 */
const YUKKA_SCORE_LIST: Array<{ id: string; accessKey: string }> = [
  { id: 'sentiment', accessKey: 'sentimentAccess' },
  { id: 'general_risk', accessKey: 'generalRiskAccess' },
  { id: 'credit_risk', accessKey: 'creditRiskAccess' },
  { id: 'esg_risk', accessKey: 'esgRiskAccess' },
  { id: 'immediate_risk', accessKey: 'immediateRiskAccess' },
  { id: 'esg', accessKey: 'esgAccess' },
  { id: 'eb_sim', accessKey: 'eb_simAccess' },
  { id: 'eb_sim_risk', accessKey: 'eb_simAccess' },
  { id: 'bb', accessKey: 'bbAccess' },
];

/**
 * Returns { name, full, short } for the user's currently-selected default
 * score. Resolves yukka scores via `getScoreTypeDetails`; falls back to the
 * matching custom score, then to the empty record.
 */
export function getScoreNameInfo(scorelabSettings: any): ScoreInfo {
  const id = scorelabSettings?.defaultScore;
  if (!id) return EMPTY_SCORE_NAME;
  return lookupScoreInfo(id, scorelabSettings);
}

/**
 * Score info: a single record when called with an id, or the full
 * { yukka, custom } catalog (filtered by access flags) when called without.
 */
export function getScoresInfo(
  scorelabSettings: any,
  id?: string,
): ScoreInfo | ScoreInfoList {
  if (id) {
    return lookupScoreInfo(id, scorelabSettings);
  }
  const scoresInfo: ScoreInfoList = { yukka: [], custom: [] };
  for (const entry of YUKKA_SCORE_LIST) {
    if (scorelabSettings?.[entry.accessKey]) {
      const d = getScoreTypeDetails(entry.id);
      scoresInfo.yukka.push({
        id: entry.id,
        name: d.label,
        full: d.label + ' Score',
        short: d.shortName,
      } as any);
    }
  }
  if (
    scorelabSettings?.customScoreAccess &&
    scorelabSettings?.customScores?.length > 0
  ) {
    for (const cs of scorelabSettings.customScores) {
      scoresInfo.custom.push({
        id: cs.uid,
        name: cs.name,
        full: cs.name + ' Custom Score',
        short: cs.name.substring(0, 5) + '...',
      } as any);
    }
  }
  return scoresInfo;
}

/**
 * Returns 'pos' / 'neg' / '' for the sentiment direction of the user's
 * default score (or `id` if provided). Required for chart coloring etc.
 */
export function getScoreType(
  scorelabSettings: any,
  id?: string,
): 'pos' | 'neg' | '' {
  if (!scorelabSettings?.defaultScore) return '';
  const scoreId = id || scorelabSettings.defaultScore;

  const details = getScoreTypeDetails(scoreId);
  if (details.sentimentType) return details.sentimentType;

  if (scorelabSettings.customScores?.length > 0) {
    const matchingScore = scorelabSettings.customScores.find(
      (score: any) => score.uid === scoreId,
    );
    if (matchingScore?.sentiment_type) return matchingScore.sentiment_type;
  }
  return '';
}

function lookupScoreInfo(id: string, scorelabSettings: any): ScoreInfo {
  if (isKnownScoreType(id)) {
    const d = getScoreTypeDetails(id);
    return { name: d.label, full: d.label + ' Score', short: d.shortName };
  }
  const customName = scorelabSettings?.customScores?.find(
    (s: any) => s.uid === id,
  )?.name;
  if (customName) {
    return {
      name: customName,
      full: customName + ' Custom Score',
      short: customName.substring(0, 5) + '...',
    };
  }
  return EMPTY_SCORE_NAME;
}

export default getScoreTypeDetails;
